import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../components/BackButton';
import SettingsButton from '../../components/SettingsButton';
import SocialButton from '../../components/SocialButton';
import HelpButton from '../../components/HelpButton';
import GameInstructionsModal from '../../components/GameInstructionsModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useSessionComms } from '../../contexts/SessionCommsContext';
import { sessionApi } from '../../services/sessionApi';
import storageManager from '../../utils/StorageManager';
import getButtonStyle from '../../utils/getButtonStyle';
import { buildBotNames, getSessionPlayerNames } from '../../utils/sessionPlayers';
import { buildInitialTrioGameState } from '../../utils/trioState';

const TRIO_MIN_PLAYERS = 3;
const TRIO_MAX_PLAYERS = 6;

const DEFAULT_TRIO_SETTINGS = {
    numCpus: 2,
    difficulty: 'easy',
    targetTrios: 2,
    enableBots: false,
};

function TrioSetup() {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { session, activeSessionContext, emitRealtimeEvent } = useSessionComms();
    const isMobile = window.innerWidth <= 768;
    const defaultPlayerName = storageManager.getPlayerName();
    const savedSettings = storageManager.getGameSettings('trio') || {};

    const [playerName, setPlayerName] = useState(savedSettings.playerName ?? defaultPlayerName);
    const [numCpus, setNumCpus] = useState(savedSettings.numCpus ?? DEFAULT_TRIO_SETTINGS.numCpus);
    const [difficulty, setDifficulty] = useState(savedSettings.difficulty ?? DEFAULT_TRIO_SETTINGS.difficulty);
    const [targetTrios, setTargetTrios] = useState(savedSettings.targetTrios ?? DEFAULT_TRIO_SETTINGS.targetTrios);
    const [enableBots, setEnableBots] = useState(savedSettings.enableBots ?? DEFAULT_TRIO_SETTINGS.enableBots);
    const [showInstructions, setShowInstructions] = useState(false);
    const [sessionStartError, setSessionStartError] = useState('');
    const sessionPlayers = useMemo(
        () => getSessionPlayerNames(session, activeSessionContext?.alias),
        [session, activeSessionContext?.alias]
    );
    const trimmedPlayerName = playerName.trim() || defaultPlayerName;
    const isSessionMode = Boolean(activeSessionContext?.code);
    const sessionCode = String(activeSessionContext?.code || '').trim().toUpperCase();
    const isSessionHost = isSessionMode
        && String(session?.hostAlias || '').trim().toLowerCase() === String(activeSessionContext?.alias || '').trim().toLowerCase();
    const hasActiveSessionGame = isSessionMode && Boolean(session?.activeVirtualGame?.active);
    const humanPlayers = useMemo(
        () => (isSessionMode ? sessionPlayers : [trimmedPlayerName].filter(Boolean)),
        [isSessionMode, sessionPlayers, trimmedPlayerName]
    );
    const humanCount = humanPlayers.length;
    const hasTooManyPlayers = humanCount > TRIO_MAX_PLAYERS;
    const maxBotsAllowed = Math.max(0, TRIO_MAX_PLAYERS - humanCount);
    const minBotsNeeded = Math.max(0, TRIO_MIN_PLAYERS - humanCount);
    const minSelectableBots = maxBotsAllowed > 0 ? Math.max(1, Math.min(minBotsNeeded, maxBotsAllowed)) : 0;
    const effectiveBotCount = enableBots ? Math.min(maxBotsAllowed, Math.max(minSelectableBots, numCpus)) : 0;
    const totalPlayers = humanCount + effectiveBotCount;
    const difficultyEnabled = enableBots && effectiveBotCount > 0;
    const hasValidName = isSessionMode || Boolean(trimmedPlayerName);
    const canStart = hasValidName
        && !hasTooManyPlayers
        && totalPlayers >= TRIO_MIN_PLAYERS
        && totalPlayers <= TRIO_MAX_PLAYERS
        && (!enableBots || (effectiveBotCount >= minSelectableBots && effectiveBotCount <= maxBotsAllowed))
        && (!isSessionMode || isSessionHost)
        && !hasActiveSessionGame;

    const instructionsSections = [
        {
            heading: 'Objective',
            paragraphs: [
                'Collect trios of matching numbers before your opponents.',
                'A trio is three cards with the same value.'
            ]
        },
        {
            heading: 'Turn Flow',
            bullets: [
                'Reveal 2 cards first. You can reveal from the table or from any player hand.',
                'For player hands, you can only pick the low or high visible edge card.',
                'If the first 2 cards match, reveal a 3rd card.',
                'If all 3 cards match, you collect 1 trio point and those cards are removed.',
                'If cards do not match, your turn ends and play passes to the next player.'
            ]
        },
        {
            heading: 'Winning',
            bullets: [
                'Reach the target trios setting to win immediately.',
                'If cards run out first, the highest trio score wins.'
            ]
        },
        {
            heading: 'Difficulty',
            paragraphs: [
                'Difficulty only changes CPU decision quality and timing.',
                'Easy CPUs make simpler reveal choices.'
            ]
        }
    ];

    useEffect(() => {
        if (hasTooManyPlayers && enableBots) {
            setEnableBots(false);
            return;
        }

        if (maxBotsAllowed === 0 && enableBots) {
            setEnableBots(false);
            return;
        }

        if (!enableBots) return;

        setNumCpus((previous) => {
            const clamped = Math.min(maxBotsAllowed, Math.max(minSelectableBots, Number(previous) || 0));
            return clamped;
        });
    }, [enableBots, hasTooManyPlayers, maxBotsAllowed, minSelectableBots]);

    useEffect(() => {
        storageManager.saveGameSettings('trio', {
            playerName: playerName.trim() || defaultPlayerName,
            numCpus,
            difficulty,
            targetTrios,
            enableBots
        });
    }, [playerName, numCpus, difficulty, targetTrios, enableBots, defaultPlayerName]);

    const startGame = async () => {
        setSessionStartError('');
        const trimmedName = playerName.trim() || defaultPlayerName;
        const roster = isSessionMode ? [...humanPlayers] : [trimmedName];
        const botCount = enableBots ? Math.min(maxBotsAllowed, Math.max(minSelectableBots, numCpus)) : 0;
        const players = [...roster, ...buildBotNames(botCount, roster)];

        if (players.length < TRIO_MIN_PLAYERS || players.length > TRIO_MAX_PLAYERS) {
            return;
        }

        storageManager.savePlayerName(trimmedName);
        storageManager.saveGameSettings('trio', {
            playerName: trimmedName,
            numCpus: botCount,
            difficulty: botCount > 0 ? difficulty : 'easy',
            targetTrios,
            enableBots
        });

        const setupPayload = {
            players,
            targetTrios,
            difficulty: botCount > 0 ? difficulty : 'easy',
            humanPlayerName: isSessionMode ? (activeSessionContext?.alias || roster[0]) : trimmedName,
            humanPlayers: roster,
        };
        let sessionSnapshot = null;

        if (isSessionMode) {
            if (!isSessionHost) {
                setSessionStartError('Only the session host can start the match.');
                return;
            }

            try {
                const initialState = buildInitialTrioGameState(players, targetTrios);
                sessionSnapshot = {
                    state: initialState,
                    currentPlayerIndex: 0,
                    reveals: [],
                    status: 'Reveal two cards. Match to continue.',
                    logEntries: [],
                    winner: null,
                    isCpuActing: false,
                };
                await sessionApi.startVirtualGame(sessionCode, {
                    startedByAlias: activeSessionContext?.alias || trimmedName,
                    game: 'trio',
                    route: '/trio-game',
                    setup: setupPayload,
                    snapshot: sessionSnapshot,
                });
                emitRealtimeEvent(
                    'session-updated',
                    { code: sessionCode, action: 'virtual-game-started', game: 'trio' },
                    sessionCode
                ).catch(() => null);
            } catch (error) {
                setSessionStartError(error.message || 'Failed to start Trio in session mode.');
                return;
            }
        }

        navigate('/trio-game', {
            state: {
                ...setupPayload,
                ...(sessionSnapshot ? { __sessionGameSnapshot: sessionSnapshot } : {}),
            },
        });
    };

    const resetSettings = () => {
        setPlayerName(defaultPlayerName);
        setNumCpus(DEFAULT_TRIO_SETTINGS.numCpus);
        setDifficulty(DEFAULT_TRIO_SETTINGS.difficulty);
        setTargetTrios(DEFAULT_TRIO_SETTINGS.targetTrios);
        setEnableBots(DEFAULT_TRIO_SETTINGS.enableBots);
        storageManager.saveGameSettings('trio', {
            ...DEFAULT_TRIO_SETTINGS,
            playerName: defaultPlayerName
        });
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: theme.bgMedium,
            color: theme.textPrimary,
            padding: '20px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
        }}>
            <BackButton to="/" />
            <HelpButton helpText="Trio allows 3-6 players. In party sessions, bots can only fill remaining seats up to 6." />
            <SettingsButton />
            <SocialButton />

            <div style={{ marginTop: isMobile ? '45px' : '20px', textAlign: 'center' }}>
                <h1 className="page-title">TRIO SETUP</h1>
                <button
                    onClick={() => setShowInstructions(true)}
                    style={{
                        marginTop: '8px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: theme.accent,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: isMobile ? '0.95rem' : '1rem'
                    }}
                >
                    See full game instructions
                </button>
            </div>

            <div style={{ display: 'grid', gap: '10px', maxWidth: '700px', width: '100%', margin: '0 auto' }}>
                <label style={{ display: 'grid', gap: '6px' }}>
                    <span>Your Name</span>
                    <input
                        value={playerName}
                        onChange={(event) => setPlayerName(event.target.value)}
                        placeholder="Player 1"
                        style={{
                            padding: '12px',
                            borderRadius: '8px',
                            border: `2px solid ${theme.secondary}`,
                            backgroundColor: theme.bgDark,
                            color: theme.textPrimary,
                            fontFamily: 'inherit',
                        }}
                    />
                </label>

                <div style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '10px',
                    padding: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <div style={{ fontWeight: 'bold' }}>Add Bots</div>
                    <button
                        onClick={() => setEnableBots((previous) => !previous)}
                        disabled={hasTooManyPlayers || maxBotsAllowed === 0}
                        style={getButtonStyle(theme, {
                            variant: enableBots ? 'success' : 'secondary',
                            padding: '8px 14px',
                            disabled: hasTooManyPlayers || maxBotsAllowed === 0
                        })}
                    >
                        {enableBots ? 'ON' : 'OFF'}
                    </button>
                </div>

                {hasTooManyPlayers ? (
                    <div style={{
                        backgroundColor: theme.bgDark,
                        border: `2px solid ${theme.danger || theme.accent}`,
                        borderRadius: '10px',
                        padding: '12px',
                        color: theme.textPrimary
                    }}>
                        Trio supports 3-6 players only. This session currently has {humanCount} players.
                    </div>
                ) : (
                    <div style={{
                        backgroundColor: theme.bgDark,
                        border: `2px solid ${theme.secondary}`,
                        borderRadius: '10px',
                        padding: '10px',
                        color: theme.textSecondary
                    }}>
                        Rule range: {TRIO_MIN_PLAYERS}-{TRIO_MAX_PLAYERS} total players.
                        {` Current roster: ${humanCount}.`}
                        {minBotsNeeded > 0 ? ` Add at least ${minBotsNeeded} bot${minBotsNeeded > 1 ? 's' : ''} to start.` : ''}
                    </div>
                )}

                <div style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '10px',
                    padding: '12px',
                    display: 'grid',
                    gap: '8px'
                }}>
                    <div style={{ fontWeight: 'bold' }}>
                        {enableBots
                            ? `CPU Opponents (${minSelectableBots}-${maxBotsAllowed})`
                            : (isSessionMode ? 'Session Players' : 'Player Roster')}
                    </div>
                    {enableBots ? (
                        <>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '14px'
                            }}>
                                <button
                                    onClick={() => setNumCpus((value) => Math.max(minSelectableBots, value - 1))}
                                    disabled={effectiveBotCount <= minSelectableBots}
                                    style={getButtonStyle(theme, { variant: 'accent', disabled: effectiveBotCount <= minSelectableBots, padding: '8px 14px' })}
                                >
                                    -
                                </button>
                                <div style={{ minWidth: '44px', textAlign: 'center', fontSize: '1.4rem', fontWeight: 'bold' }}>{effectiveBotCount}</div>
                                <button
                                    onClick={() => setNumCpus((value) => Math.min(maxBotsAllowed, value + 1))}
                                    disabled={effectiveBotCount >= maxBotsAllowed}
                                    style={getButtonStyle(theme, { variant: 'accent', disabled: effectiveBotCount >= maxBotsAllowed, padding: '8px 14px' })}
                                >
                                    +
                                </button>
                            </div>
                            <div style={{ textAlign: 'center', color: theme.textSecondary }}>
                                Total players: {totalPlayers}
                            </div>
                        </>
                    ) : (
                        <div style={{ display: 'grid', gap: '6px', color: theme.textSecondary }}>
                            {humanPlayers.length > 0 ? (
                                humanPlayers.map((name, index) => (
                                    <div key={`${name}-${index}`}>{index + 1}. {name}</div>
                                ))
                            ) : (
                                <div>Add a player name and enable bots to create a 3-6 player match.</div>
                            )}
                        </div>
                    )}
                </div>

                <div style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '10px',
                    padding: '12px',
                    display: 'grid',
                    gap: '8px',
                    opacity: difficultyEnabled ? 1 : 0.6
                }}>
                    <div style={{ fontWeight: 'bold' }}>Difficulty (Bots Only)</div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '8px' }}>
                        {['easy', 'medium', 'hard'].map((level) => (
                            <button
                                key={level}
                                onClick={() => setDifficulty(level)}
                                disabled={!difficultyEnabled}
                                style={getButtonStyle(theme, {
                                    variant: difficulty === level ? 'accent' : 'secondary',
                                    padding: '10px',
                                    disabled: !difficultyEnabled
                                })}
                            >
                                {level.toUpperCase()}
                            </button>
                        ))}
                    </div>
                    {!difficultyEnabled && (
                        <div style={{ color: theme.textSecondary, fontSize: '0.9rem' }}>
                            Enable bots to use difficulty settings.
                        </div>
                    )}
                </div>

                <label style={{ display: 'grid', gap: '6px' }}>
                    <span>Trios needed to win</span>
                    <input
                        type="number"
                        min={1}
                        max={6}
                        value={targetTrios}
                        onChange={(event) => setTargetTrios(Math.min(6, Math.max(1, Number(event.target.value) || 2)))}
                        style={{
                            padding: '10px',
                            borderRadius: '8px',
                            border: `2px solid ${theme.secondary}`,
                            backgroundColor: theme.bgDark,
                            color: theme.textPrimary,
                            fontFamily: 'inherit',
                        }}
                    />
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {[1, 2, 3, 4, 5, 6].map((value) => (
                            <button
                                key={`target-${value}`}
                                type="button"
                                onClick={() => setTargetTrios(value)}
                                style={getButtonStyle(theme, {
                                    variant: targetTrios === value ? 'accent' : 'secondary',
                                    padding: '6px 10px'
                                })}
                            >
                                {value}
                            </button>
                        ))}
                    </div>
                </label>

                <button
                    onClick={resetSettings}
                    style={getButtonStyle(theme, { variant: 'secondary', outline: true, padding: '12px 16px' })}
                >
                    Reset to Default
                </button>

                <button
                    onClick={startGame}
                    disabled={!canStart}
                    style={getButtonStyle(theme, {
                        variant: 'accent',
                        disabled: !canStart,
                        padding: '14px 20px',
                        borderRadius: '10px'
                    })}
                >
                    Start Trio
                </button>
                {isSessionMode && !isSessionHost && (
                    <div style={{ color: theme.textSecondary, fontSize: '0.9rem' }}>
                        Session mode is host-controlled. Waiting for host to start.
                    </div>
                )}
                {hasActiveSessionGame && (
                    <div style={{ color: theme.warning || theme.textSecondary, fontSize: '0.9rem' }}>
                        A session game is already active. End it before starting another.
                    </div>
                )}
                {sessionStartError && (
                    <div style={{ color: theme.danger || theme.accent, fontSize: '0.9rem' }}>
                        {sessionStartError}
                    </div>
                )}
            </div>

            <GameInstructionsModal
                isOpen={showInstructions}
                onClose={() => setShowInstructions(false)}
                title="Trio - Full Instructions"
                sections={instructionsSections}
                closeLabel="Close Instructions"
            />
        </div>
    );
}

export default TrioSetup;
